import React, { useState, useEffect, useMemo, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { Edit, Trash, Eye, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DateRangeFilter from '../DateRangeFilter';
import FiltersPanel from '../FiltersPanel';
import { t, useLanguage } from '../../i18n';

const apiUrl = process.env.REACT_APP_API_URL;

export default function InvoiceList() {
  const { lang } = useLanguage();
  const [invoices, setInvoices] = useState([]);
  const [partners, setPartners] = useState([]);
  const [filter, setFilter] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [datePreset, setDatePreset] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [hoveredRow, setHoveredRow] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState({});
  const [loadingItems, setLoadingItems] = useState(new Set());
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const fetchPartners = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await fetch(`${apiUrl}/partners?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch partners');
      }

      const partnersData = await response.json();
      setPartners(Array.isArray(partnersData) ? partnersData : []);
    } catch (error) {
      console.error('Error fetching partners:', error);
      setPartners([]);
    }
  };

  const fetchInvoices = async (filterParams = {}) => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('Потребна е најава. Ве молиме најавете се.'));
        setInvoices([]);
        return;
      }

      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filterParams.search) queryParams.append('search', filterParams.search);
      if (filterParams.partner) queryParams.append('partner', filterParams.partner);
      if (filterParams.dateFrom) queryParams.append('dateFrom', filterParams.dateFrom);
      if (filterParams.dateTo) queryParams.append('dateTo', filterParams.dateTo);

      const url = `${apiUrl}/invoices${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(t('Неуспешно вчитување на фактурите: {status} {statusText}', { status: response.status, statusText: response.statusText }));
      }

      const result = await response.json();
      console.log('Invoices API response:', result);
      
      // The API returns {data: [...], pagination: {...}}
      setInvoices(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      setError(error.message);
      setInvoices([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
    fetchInvoices();
  }, []);

  // Apply filters on backend
  const applyFilters = () => {
    const filterParams = {};
    if (filter) filterParams.search = filter;
    if (partnerFilter) filterParams.partner = partnerFilter;
    if (dateFromFilter) filterParams.dateFrom = dateFromFilter;
    if (dateToFilter) filterParams.dateTo = dateToFilter;
    
    fetchInvoices(filterParams);
  };

  const fetchInvoiceItems = async (invoiceId) => {
    // Avoid fetching if already loaded or currently loading
    if (invoiceItems[invoiceId] || loadingItems.has(invoiceId)) {
      return;
    }

    setLoadingItems(prev => new Set([...prev, invoiceId]));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/invoice-view/${invoiceId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInvoiceItems(prev => ({
          ...prev,
          [invoiceId]: data.items || []
        }));
      } else {
        console.error('Failed to fetch invoice items:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching invoice items:', error);
    } finally {
      setLoadingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoiceId);
        return newSet;
      });
    }
  };

  const handleEditClick = (id) => {
    navigate(`/invoices/edit/${id}`);
  };

  const handleDeleteClick = (id) => {
    navigate(`/invoices/delete/${id}`);
  };

  const handlePreviewClick = (id) => {
    navigate(`/invoices/view/${id}`);
  };

      const handlePreviewPdf = (invoiceId, pdfFileName, companyId) => {
    if (!pdfFileName) {
      alert(t('Нема достапен PDF за оваа фактура'));
      return;
    }

    const previewUrl = `/preview/${companyId}/invoices/${pdfFileName}`;
    window.open(previewUrl, '_blank');
  };

  const allColumns = useMemo(() => [
    {
      id: 'index',
      name: '#',
      cell: (row, index) => (
        <span className="font-semibold text-gray-600">{index + 1}</span>
      ),
      width: '50px',
      sortable: false,
    },
    { 
      id: 'number',
      name: t('Број на фактура'),
      selector: (row) => row.invoice_number, 
      sortable: true,
      cell: (row) => <span className="font-semibold text-gray-900">{row.invoice_number}</span>
    },
    { 
      id: 'recipient',
      name: t('Примач'),
      selector: (row) => row.partner_name, 
      sortable: true,
      grow: 2,
    },
    {
      id: 'date',
      name: t('Датум на фактура'),
      selector: (row) =>
        row.date
          ? new Date(row.date).toLocaleDateString('mk-MK')
          : '-',
      sortable: true,
      width: '140px',
    },
    { 
      id: 'due',
      name: t('Рок за плаќање'),
      selector: (row) => row.payment_due_days, 
      sortable: true,
      width: '140px',
    },
    { 
      id: 'total',
      name: t('Вкупна сума'),
      selector: (row) => row.total, 
      sortable: true,
      right: true,
      width: '140px',
      cell: (row) => <span className="font-semibold text-gray-900">{parseFloat(row.total || 0).toFixed(2)} ден.</span>
    },
    {
      id: 'status',
      name: t('Статус'),
      selector: (row) => row.status,
      sortable: true,
      width: '120px',
      cell: (row) => (
        <span
          className={`px-3 py-1 rounded-full text-xs font-semibold inline-block
            ${
              row.status === 'complete'
                ? 'bg-green-100 text-green-700'
                : row.status === 'draft'
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-gray-100 text-gray-700'
            }`
          }
        >
          {row.status === 'complete' ? t('Завршена') : row.status === 'draft' ? t('Нацрт') : row.status}
        </span>
      ),
    },
    {
      id: 'created',
      name: t('Креирана на'),
      selector: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString('mk-MK') : ''),
      sortable: true,
      width: '140px',
    },
    {
      id: 'actions',
      name: t('Акции'),
      cell: (row) => {
        const actualId = row.id;
        return (
          <div className="flex gap-2 items-center">
            <button 
              onClick={() => handlePreviewPdf(actualId, row.pdf_filename, row.occupier)} 
              title={t('Прегледај PDF')}
              disabled={!row.pdf_filename}
              className={`p-2 rounded-lg transition-colors ${
                row.pdf_filename 
                  ? 'text-green-600 hover:bg-green-50' 
                  : 'text-gray-400 cursor-not-allowed'
              }`}
            >
              <Eye size={18} />
            </button>
            <button 
              onClick={() => handlePreviewClick(row.id)} 
              title={t('Погледни детали и плаќања')}
              className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
            >
              <FileText size={18} />
            </button>
            <button 
              onClick={() => handleEditClick(row.id)} 
              title={t('Уреди')}
              className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors"
            >
              <Edit size={18} />
            </button>
            <button 
              onClick={() => handleDeleteClick(row.id)} 
              title={t('Избриши')}
              className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash size={18} />
            </button>
          </div>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '160px',
    },
  ], [expandedRows, lang]);

  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.map((col) => col.id).filter((id) => ['index', 'number', 'date', 'recipient', 'total', 'status', 'actions'].includes(id))
  );

  const toggleColumn = (columnName) => {
    setVisibleColumns((prev) =>
      prev.includes(columnName) ? prev.filter((name) => name !== columnName) : [...prev, columnName]
    );
  };

  const columns = useMemo(() => allColumns.filter((col) => visibleColumns.includes(col.id)), [allColumns, visibleColumns]);

  // Use all partners for dropdown
  const uniquePartners = useMemo(() => {
    if (!Array.isArray(partners)) return [];
    return partners
      .filter(partner => partner.name && partner.name.trim() !== '')
      .map(partner => ({ id: partner.id, name: partner.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [partners]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownRef]);

  if (loading) {
    return (
      <div className="h-full bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 mx-auto text-blue-500 mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600 text-lg">{t('Вчитување на фактури...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full bg-gray-50 p-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-red-900">{t('Грешка при вчитување на фактурите')}</h3>
                <p className="mt-2 text-sm text-red-700">{error}</p>
              </div>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              {t('Обиди се повторно')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Filters Section */}
        <FiltersPanel>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
            {/* General Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('Пребарај')}</label>
              <input
                type="text"
                placeholder={t('Број на фактура...')}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Partner Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('Партнер')}</label>
              <select
                value={partnerFilter}
                onChange={(e) => setPartnerFilter(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
              >
                <option value="">{t('Сите партнери')}</option>
                {uniquePartners.map((partner) => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <DateRangeFilter
              preset={datePreset}
              setPreset={setDatePreset}
              dateFrom={dateFromFilter}
              setDateFrom={setDateFromFilter}
              dateTo={dateToFilter}
              setDateTo={setDateToFilter}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 justify-start sm:justify-end">
            <button
              onClick={() => {
                setFilter('');
                setPartnerFilter('');
                setDateFromFilter('');
                setDateToFilter('');
                setDatePreset('');
                fetchInvoices();
              }}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium text-sm transition-colors flex items-center space-x-1.5"
              title={t('Избриши ги сите филтери')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>{t('Избриши')}</span>
            </button>
            <button
              onClick={applyFilters}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm transition-colors flex items-center space-x-1.5"
              title={t('Примени филтери')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span>{t('Филтрирај')}</span>
            </button>
          </div>
                </FiltersPanel>

        {/* Action Bar */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6 justify-start sm:justify-end">
          <div className="flex gap-2 items-center flex-wrap justify-start sm:justify-end w-full sm:w-auto">
            <button
              onClick={() => navigate('/invoices/create')}
              className="w-full sm:w-auto justify-center px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-sm transition-colors flex items-center space-x-2"
              title={t('Нова фактура')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>{t('Нова фактура')}</span>
            </button>
          </div>
        </div>

        {/* Invoices Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-full">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900">{t('Фактури')}</h2>
            <div className="flex gap-2 items-center">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium text-sm transition-colors flex items-center space-x-1.5"
                  title={t('Избери колони')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4v16M15 4v16" />
                  </svg>
                  <span className="hidden sm:inline">{t('Колони')}</span>
                </button>

                {showDropdown && (
                  <div className="absolute right-0 w-56 bg-white border border-gray-300 rounded-lg shadow-lg z-50 mt-2">
                    <div className="p-3">
                      <p className="text-xs font-semibold text-gray-600 mb-2 uppercase">{t('Прикажи колони')}</p>
                      <div className="space-y-2 max-h-64 overflow-auto">
                        {allColumns.map((col) => (
                          <label key={col.id} className="flex items-center cursor-pointer hover:bg-blue-50 p-2 rounded transition-colors">
                            <input
                              type="checkbox"
                              checked={visibleColumns.includes(col.id)}
                              onChange={() => toggleColumn(col.id)}
                              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-0"
                            />
                            <span className="ml-3 text-sm text-gray-700">{col.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-800 text-white rounded-md font-medium text-sm transition-colors flex items-center space-x-1.5"
                onClick={applyFilters}
                disabled={loading}
                title={t('Освежи ја листата')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="hidden sm:inline">{loading ? t('Вчитување...') : t('Освежи')}</span>
              </button>

              <CSVLink
                data={invoices}
                filename="invoices.csv"
                target="_blank"
                className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-md font-medium text-sm transition-colors no-underline flex items-center space-x-1.5"
                title={t('Експортирај ги фактурите во CSV')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span className="hidden sm:inline">{t('Експорт')}</span>
              </CSVLink>
            </div>
          </div>
          <DataTable
            columns={columns}
            data={invoices}
            pagination
            highlightOnHover={false}
            striped={false}
            responsive
            persistTableHead
            noDataComponent={
              <div className="p-12 text-center">
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-600">{t('Нема пронајдени фактури')}</p>
              </div>
            }
            conditionalRowStyles={[
              {
                when: row => expandedRows.has(row.id),
                style: {
                  backgroundColor: '#eff6ff',
                  borderLeft: '4px solid #3b82f6'
                }
              },
              {
                when: row => hoveredRow === row.id && !expandedRows.has(row.id),
                style: {
                  backgroundColor: '#f9fafb',
                }
              }
            ]}
            onRowMouseEnter={(row) => setHoveredRow(row.id)}
            onRowMouseLeave={() => setHoveredRow(null)}
            expandableRows
            expandableRowsComponent={({ data }) => {
              const actualId = data.id;
              const backgroundColor = expandedRows.has(actualId) ? '#eff6ff' : '#f9fafb';
              
              return (
                <div 
                  className="border-t border-gray-200 bg-blue-50"
                  onMouseEnter={() => setHoveredRow(actualId)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  {loadingItems.has(actualId) ? (
                    <div className="text-center py-8">
                      <svg className="animate-spin h-6 w-6 mx-auto text-blue-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <p className="mt-2 text-sm text-gray-600">{t('Вчитување на ставки...')}</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-blue-100 border-b border-blue-200">
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">#</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('Назив на производ')}</th>
                            <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('Количина')}</th>
                            <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('Цена')}</th>
                            <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('Вкупно')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(invoiceItems[actualId] || []).length > 0 ? (
                            (invoiceItems[actualId] || []).map((item, index) => (
                              <tr 
                                key={item.id || index} 
                                className="border-b border-gray-200 hover:bg-blue-100 transition-colors"
                              >
                                <td className="px-6 py-3 text-sm text-gray-600 font-medium">
                                  {index + 1}
                                </td>
                                <td className="px-6 py-3 text-sm text-gray-900">
                                  {item.product_name || t('Непознат производ')}
                                </td>
                                <td className="px-6 py-3 text-sm text-gray-900 text-center">
                                  {item.quantity}
                                </td>
                                <td className="px-6 py-3 text-sm text-gray-900 text-center">
                                  {parseFloat(item.price || 0).toFixed(2)} ден.
                                </td>
                                <td className="px-6 py-3 text-sm text-gray-900 text-center font-semibold">
                                  {(parseFloat(item.quantity || 0) * parseFloat(item.price || 0)).toFixed(2)} ден.
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="px-6 py-8 text-center text-sm text-gray-500">
                                {t('Нема пронајдени ставки за оваа фактура')}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            }}
            expandableRowExpanded={(row) => {
              return expandedRows.has(row.id);
            }}
            onRowExpandToggled={(expanded, row) => {
              if (expanded) {
                setExpandedRows(prev => new Set([...prev, row.id]));
                if (!invoiceItems[row.id]) {
                  fetchInvoiceItems(row.id);
                }
              } else {
                setExpandedRows(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(row.id);
                  return newSet;
                });
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}
